import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import ReportsSubSidebar from "../components/ReportsSubSidebar";
import SettingsSubSidebar from "../components/SettingsSubSidebar";
import WebsiteSubSidebar from "../components/WebsiteSubSidebar";
import MarketingSubSidebar from "../components/MarketingSubSidebar";
import { AnimatePresence } from "framer-motion";

// Pages
import Dashboard from "../pages/Dashboard";
import BookingsPage from "../pages/Bookings";
import POS from "../pages/POS";
import Website from "../pages/website/Website";
import Reports from "../pages/Reports";
import Manage from "../pages/Manage";
import Customers from "../pages/Customers";
import Appointments from "../pages/customers/Appointments";
import CustomersSubSidebar from "../components/CustomersSubSidebar";
import Upgrades from "../pages/Upgrades";
import Settings from "../pages/Settings";
import CashUpDetail from "../components/CashUpDetail";
import DailySalesPage from "../pages/reports/DailySalesPage";
import ManageSubSidebar from "../components/ManageSubSidebar";
import ForgotPassword from "../pages/auth/forgot-password";
import Login from "../pages/auth/login";
import ConsultationsPage from "../pages/Consultations";
import ConsultationResponsePage from "../pages/ConsultationResponsePage";
import FormTemplatesPage from "../pages/FormTemplatePage";
import { AuthProvider, useAuth } from "../auth/AuthContext";
import {
  getRequiredPackageModuleForPath,
  getDefaultLandingPath,
  hasPackageModuleAccess,
} from "../lib/packageModules";
import { SunbedSessionsProvider } from "../context/SunbedSessionsContext";
import { SunbedTmaxJobPoller } from "../components/SunbedTmaxJobPoller";
import CategoriesPage from "../pages/manage/CategoriesPage";
import CategoriesPageNew from "../pages/manage/CategoriesPageNew";
import ServicesPage from "../pages/manage/ServicesPage";
import EmployeePage from "../pages/manage/EmployeePage";
import ScheduledShiftsPage from "../pages/manage/ScheduledShiftsPage";
import TimesheetPage from "../pages/manage/TimesheetPage";
import ServiceSettings from "../pages/ServiceSettings";
import ProductsPage from "../pages/manage/products/ProductsPage";
import SectionSettings from "../pages/website/SectionSettings";
import HeroSection from "../pages/website/HeroSection";
import Microsite from "../pages/website/Microsite";
import CalenderPage from "../pages/Calender";

// Settings Components
import ProfileSettings from "../pages/settings/ProfileSettings";
import GeneralSettings from "../pages/settings/GeneralSettings";
import AttachDomainSettings from "../pages/settings/AttachDomainSettings";
import XONDERSettings from "../pages/settings/XONDERSettings";
import VendorPageSettings from "../pages/settings/VendorPageSettings";
import BookingsSettings from "../pages/settings/BookingsSettings";
import NotificationSettings from "../pages/settings/NotificationSettings";
import QuickPaymentsSettings from "../pages/settings/QuickPaymentsSettings";
import SunbedSettings from "../pages/settings/SunbedSettings";
import PrinterSettings from "../pages/settings/PrinterSettings";
import CashDrawerSettings from "../pages/settings/CashDrawerSettings";
import KioskSettings from "../pages/settings/KioskSettings";
import CustomerRegistrationSettings from "../pages/settings/CustomerRegistrationSettings";
import BillingSettings from "../pages/settings/BillingSettings";

// Contexts
import { SectionProvider } from "../context/SectionContext";
import { StepProvider } from "../context/StepContext";
import SuccessPage from "../pages/website/SuccessPage";
import FormBuilder from "../pages/forms/[id]/builder/FormBuilder";
import PublicFormPage from "../pages/PublicFormPage";
import GallerySection from "../pages/website/GallerySection";
import FooterSection from "../pages/website/FooterSection";
import AboutSection from "../pages/website/AboutSection";
import ServiceSection from "../pages/website/ServiceSection";
import TestimonialsSection from "../pages/website/TestomonialSection";
import FontandThemeSection from "../pages/website/FontandThemeSection";
import AboutUsPage from "../pages/website/AboutUsPage";
import CancelPolicyPage from "../pages/website/CancelPolicyPage";
import PrivacyPolicyPage from "../pages/website/PrivacyPolicyPage";
import TermsConditionsPage from "../pages/website/TermsConditionsPage";
import TemplateSection from "../pages/website/TemplateSection";
import PublicConsultationPage from "../pages/PublicConsultationPage";
import PublicCustomerRegistrationPage from "../pages/PublicCustomerRegistrationPage";
import BusinessProposalPage from "../pages/BusinessProposalPage";

// Auth Components
import PrivateRoute from "../components/PrivateRoute";
import CustomerDisplay from "../pages/pos/CustomerDisplay";
import PublicRoute from "../components/PublicRoute";
import SunbedSubSidebar from "../components/SunbedSubSidebar";
import SunbedPackages from "../pages/sunbeds/SunbedPackages";
import SunbedMembershipPlans from "../pages/sunbeds/SunbedMembershipPlans";
import SunbedsPage from "../pages/sunbeds/SunbedsPage";
import CustomerPackages from "../pages/sunbeds/CustomerPackages";
import SunbedSessionPage from "../pages/SunbedSessionPage";
import SunbedWaitingListPage from "../pages/SunbedWaitingListPage";
import ZReport from "../pages/reports/ZReport";
import ZReportDetail from "../pages/reports/ZReportDetail";
import XReport from "../pages/reports/XReport";
import SunbedReport from "../pages/reports/SunbedReport";
import ReportsDashboard from "../pages/reports/ReportsDashboard";
import SalesPage from "../pages/reports/SalesPage";
import PaymentsPage from "../pages/reports/PaymentsPage";
import AppointmentsReportPage from "../pages/reports/AppointmentsReportPage";
import CustomerReportPage from "../pages/reports/CustomerReportPage";

// Summary Pages
import EmployeesSummaryPage from "../pages/summary/EmployeesSummaryPage";
import ServicesSummaryPage from "../pages/summary/ServicesSummaryPage";
import ProductsSummaryPage from "../pages/summary/ProductsSummaryPage";
import CustomersSummaryPage from "../pages/summary/CustomersSummaryPage";
import BookingsSummaryPage from "../pages/summary/BookingsSummaryPage";
import CustomerDetailsPage from "../pages/summary/details/CustomerDetailsPage";
import EmployeeDetailsPage from "../pages/summary/details/EmployeeDetailsPage";
import ServiceDetailsPage from "../pages/summary/details/ServiceDetailPage";
import ProductDetailsPage from "../pages/summary/details/ProductDetailsPage";
import BookingDetailsPage from "../pages/summary/details/BookingDetailsPage";
import SoldProductsPage from "../pages/manage/products/SoldProductsPage";
import ServiceSalesReportPage from "../pages/reports/ServiceSalesReportPage";
import EmployeeDashboard from "../pages/EmployeeDashboard";
import MarketingLayout from "../pages/marketing/MarketingLayout";
import PaymentsLayout from "../pages/payments/PaymentsLayout";
import SuperAdminDashboard from "../pages/superadmin/SuperAdminDashboard";
import SuperAdminCompanies from "../pages/superadmin/SuperAdminCompanies";
import SuperAdminCompanyCreate from "../pages/superadmin/SuperAdminCompanyCreate";
import SuperAdminClaimBusinesses from "../pages/superadmin/SuperAdminClaimBusinesses";
import SuperAdminCompanyClaims from "../pages/superadmin/SuperAdminCompanyClaims";
import SuperAdminPackages from "../pages/superadmin/SuperAdminPackages";
import SuperAdminPackageForm from "../pages/superadmin/SuperAdminPackageForm";
import SuperAdminInvoices from "../pages/superadmin/SuperAdminInvoices";
import SuperAdminOfflineRequests from "../pages/superadmin/SuperAdminOfflineRequests";
import SuperAdminLocations from "../pages/superadmin/SuperAdminLocations";
import SuperAdminLocationForm from "../pages/superadmin/SuperAdminLocationForm";
import SuperAdminCategories from "../pages/superadmin/SuperAdminCategories";
import SuperAdminReports from "../pages/superadmin/SuperAdminReports";
import SuperAdminSettings from "../pages/superadmin/SuperAdminSettings";
import SuperAdminLoginLogs from "../pages/superadmin/SuperAdminLoginLogs";
import SuperAdminUserActivityLogs from "../pages/superadmin/SuperAdminUserActivityLogs";
import CouponsPage from "../pages/manage/CouponsPage";
import CouponFormPage from "../pages/manage/CouponFormPage";
import DealsPage from "../pages/manage/DealsPage";

const LAYOUT_BREAKPOINT = 1030; // px — sidebar expands and layout switches to side-by-side

type PackageModuleGuardProps = {
  moduleLabel: string;
  children: React.ReactElement;
};

const PackageModuleGuard = ({ moduleLabel, children }: PackageModuleGuardProps) => {
  const { user } = useAuth();

  if (hasPackageModuleAccess(user, moduleLabel)) {
    return children;
  }

  return <Navigate to={getDefaultLandingPath(user)} replace />;
};

const RoleDashboardRoute = () => {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isSuperAdmin = roles.some((r: { name?: string }) => r?.name === "superadmin" || r?.name === "agent");
  const isEmployee = roles.some((r: { name?: string }) => r?.name === "employee");

  if (isSuperAdmin) return <Navigate to="/superadmin/dashboard" replace />;
  if (isEmployee) return <Navigate to="/employee/dashboard" replace />;

  const defaultLanding = getDefaultLandingPath(user);
  if (defaultLanding === "/pos") {
    return <Navigate to="/pos" replace />;
  }

  return <Dashboard />;
};

const SuperAdminRoute = ({ children }: { children: React.ReactElement }) => {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isSuperAdmin = roles.some((r: { name?: string }) => r?.name === "superadmin" || r?.name === "agent");
  const isEmployee = roles.some((r: { name?: string }) => r?.name === "employee");

  if (!isSuperAdmin) {
    return <Navigate to={isEmployee ? "/employee/dashboard" : "/dashboard"} replace />;
  }

  return children;
};

// -------------------------------
// Layout wrapper (for app pages only)
// -------------------------------
const Layout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= LAYOUT_BREAKPOINT);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= LAYOUT_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const required = getRequiredPackageModuleForPath(path);
    if (!required) return;
    if (hasPackageModuleAccess(user, required)) return;
    navigate(getDefaultLandingPath(user), { replace: true });
  }, [location.pathname, user, navigate]);

  const isBookings = location.pathname.startsWith("/bookings");
  const isReports =
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/cashup");
  const isCustomers =
    location.pathname.startsWith("/customers") ||
    location.pathname.startsWith("/appointments");
  const isDailySales = location.pathname.startsWith("/daily-sales");
  const isManage = location.pathname.startsWith("/manage");
  const isServices = location.pathname.startsWith("/services");
  const isCategories = location.pathname.startsWith("/categories");
  const isEmployees = location.pathname.startsWith("/manage/employee");
  const isWebsiteModule = location.pathname.startsWith("/website");
  const isSettings = location.pathname.startsWith("/settings");
  const isSunbeds = location.pathname.startsWith("/sunbeds");
  const isMarketing = location.pathname.startsWith("/marketing");
  const isPOS = location.pathname.startsWith("/pos");

  return (
    <div className="flex h-screen h-[100dvh] overflow-hidden">
      {/* Main Sidebar */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isDesktop={isDesktop} />
      <div className={isDesktop ? "block" : "hidden"}>
        {(isManage || isServices || isCategories || isEmployees) && (
          <ManageSubSidebar />
        )}

        {/* Sub Sidebar only for Reports */}
        {isReports && <ReportsSubSidebar />}

        {/* Sub Sidebar only for Settings */}
        {isSettings && <SettingsSubSidebar />}

        {/* Sub Sidebar only for Sunbeds */}
        {isSunbeds && <SunbedSubSidebar />}

        {/* Sub Sidebar only for Website */}
        {isWebsiteModule && <WebsiteSubSidebar />}

        {/* Sub Sidebar only for Marketing */}
        {isMarketing && <MarketingSubSidebar />}
      </div>

      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <Navbar onMobileMenuClick={() => setIsSidebarOpen(true)} />
        <div className={`flex flex-1 ${isDesktop ? "flex-row" : "flex-col"} min-h-0`}>
          {/* Sub Sidebar only for Manage — visible below 1030px so tabs/small layout matches main breakpoint */}
          <div className="layout:hidden flex-shrink-0">
            {(isManage || isServices || isCategories || isEmployees) && (
              <ManageSubSidebar />
            )}

            {/* Sub Sidebar only for Reports */}
            {isReports && <ReportsSubSidebar />}

            {/* Sub Sidebar only for Settings */}
            {isSettings && <SettingsSubSidebar />}

            {/* Sub Sidebar only for Sunbeds */}
            {isSunbeds && <SunbedSubSidebar />}

            {/* Sub Sidebar only for Website */}
            {isWebsiteModule && <WebsiteSubSidebar />}

            {/* Sub Sidebar only for Marketing */}
            {isMarketing && <MarketingSubSidebar />}
          </div>

          <div
            className={`flex-1 min-h-0 bg-gray-100 ${
              isPOS ? "overflow-hidden" : "overflow-y-auto"
            } ${
              isDailySales || isManage
                ? "pt-0 pr-0 pb-3 pl-0"
                : isReports
                  ? "pt-0 pr-3 pb-3 pl-3"
                  : isBookings
                    ? "pt-0 pr-6 pb-6 pl-6"
                    : isWebsiteModule
                      ? "pt-4 pr-6 pb-6 pl-6"
                      : location.pathname.startsWith("/dashboard") ||
                          location.pathname.startsWith("/employee/dashboard") ||
                          location.pathname === "/"
                        ? "pt-0 pr-0 pb-6 pl-0"
                        : "pt-4 pr-6 pb-6 pl-6"
            }`}
          >
            <AnimatePresence mode="wait">
              <Routes
                location={location}
                key={
                  location.pathname.startsWith("/settings")
                    ? "/settings"
                    : location.pathname.startsWith("/website")
                      ? "/website"
                      : location.pathname
                }
              >
                <Route path="/" element={<RoleDashboardRoute />} />
                <Route path="/dashboard" element={<RoleDashboardRoute />} />
                <Route
                  path="/employee/dashboard"
                  element={<EmployeeDashboard />}
                />
                <Route path="/superadmin/dashboard" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
                <Route path="/superadmin/companies" element={<SuperAdminRoute><SuperAdminCompanies /></SuperAdminRoute>} />
                <Route path="/superadmin/companies/create" element={<SuperAdminRoute><SuperAdminCompanyCreate /></SuperAdminRoute>} />
                <Route path="/superadmin/companies/:id/edit" element={<SuperAdminRoute><SuperAdminCompanyCreate /></SuperAdminRoute>} />
                <Route path="/superadmin/companies/claimed-businesses" element={<SuperAdminRoute><SuperAdminClaimBusinesses /></SuperAdminRoute>} />
                <Route path="/superadmin/companies/claims" element={<SuperAdminRoute><SuperAdminCompanyClaims /></SuperAdminRoute>} />
                <Route path="/superadmin/packages" element={<SuperAdminRoute><SuperAdminPackages /></SuperAdminRoute>} />
                <Route path="/superadmin/packages/create" element={<SuperAdminRoute><SuperAdminPackageForm /></SuperAdminRoute>} />
                <Route path="/superadmin/packages/:id/edit" element={<SuperAdminRoute><SuperAdminPackageForm /></SuperAdminRoute>} />
                <Route path="/superadmin/invoices" element={<SuperAdminRoute><SuperAdminInvoices /></SuperAdminRoute>} />
                <Route path="/superadmin/offline-requests" element={<SuperAdminRoute><SuperAdminOfflineRequests /></SuperAdminRoute>} />
                <Route path="/superadmin/locations" element={<SuperAdminRoute><SuperAdminLocations /></SuperAdminRoute>} />
                <Route path="/superadmin/locations/create" element={<SuperAdminRoute><SuperAdminLocationForm /></SuperAdminRoute>} />
                <Route path="/superadmin/locations/:id/edit" element={<SuperAdminRoute><SuperAdminLocationForm /></SuperAdminRoute>} />
                <Route path="/superadmin/categories" element={<SuperAdminRoute><SuperAdminCategories /></SuperAdminRoute>} />
                <Route path="/superadmin/reports" element={<SuperAdminRoute><SuperAdminReports /></SuperAdminRoute>} />
                <Route path="/superadmin/login-logs" element={<SuperAdminRoute><SuperAdminLoginLogs /></SuperAdminRoute>} />
                <Route path="/superadmin/user-activity-logs" element={<SuperAdminRoute><SuperAdminUserActivityLogs /></SuperAdminRoute>} />
                <Route path="/superadmin/settings" element={<SuperAdminRoute><SuperAdminSettings /></SuperAdminRoute>} />

                {/* Summary Routes */}
                <Route
                  path="/summary/employees"
                  element={<EmployeesSummaryPage />}
                />
                <Route
                  path="/summary/services"
                  element={<ServicesSummaryPage />}
                />
                <Route
                  path="/summary/products"
                  element={<ProductsSummaryPage />}
                />
                <Route
                  path="/summary/customers"
                  element={<CustomersSummaryPage />}
                />
                <Route
                  path="/summary/bookings"
                  element={<BookingsSummaryPage />}
                />

                {/* Summary Details Routes */}
                <Route
                  path="/summary/customers/:id"
                  element={<CustomerDetailsPage />}
                />
                <Route
                  path="/summary/employees/:id"
                  element={<EmployeeDetailsPage />}
                />
                <Route
                  path="/summary/services/:id"
                  element={<ServiceDetailsPage />}
                />
                <Route
                  path="/summary/products/:id"
                  element={<ProductDetailsPage />}
                />
                <Route
                  path="/summary/bookings/:id"
                  element={<BookingDetailsPage />}
                />

                <Route path="/calendar" element={<CalenderPage />} />
                <Route path="/bookings" element={<BookingsPage />} />
                <Route path="/appointments" element={<Appointments />} />
                <Route
                  path="/pos"
                  element={
                    <PackageModuleGuard moduleLabel="POS">
                      <POS />
                    </PackageModuleGuard>
                  }
                />
                <Route path="/website" element={<Website />} />
                <Route path="/marketing/*" element={<MarketingLayout />} />
                <Route path="/payments/*" element={<PaymentsLayout />} />

                <Route
                  path="/consultation"
                  element={
                    <PackageModuleGuard moduleLabel="Zoom Meeting">
                      <ConsultationsPage />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/consultation-response/:id"
                  element={
                    <PackageModuleGuard moduleLabel="Zoom Meeting">
                      <ConsultationResponsePage />
                    </PackageModuleGuard>
                  }
                />
                {/* Please keep  it here  */}
                <Route
                  path="form-builder/:userId/:templateId"
                  element={<FormBuilder />}
                />
                <Route
                  path="/form-templates"
                  element={
                    <FormTemplatesPage
                    // onNavigateBack={() => window.history.back()}
                    />
                  }
                />
                <Route path="/form-templates" element={<FormTemplatesPage />} />
                <Route
                  path="/cashup/:id"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <CashUpDetail />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/daily-sales"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <DailySalesPage />
                    </PackageModuleGuard>
                  }
                />

                {/* Reports Routes */}
                <Route
                  path="/reports/dashboard"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <ReportsDashboard />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/sales"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <Navigate to="/reports/product-sales" replace />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/product-sales"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <SoldProductsPage />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/service-sales"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <ServiceSalesReportPage />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/payments"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <PaymentsPage />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/appointments"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <AppointmentsReportPage />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/z-report"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <ZReport />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/x-report"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <XReport />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/sunbed-report"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <SunbedReport />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/customer-report"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <CustomerReportPage />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <Navigate to="/reports/product-sales" replace />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/reports/z-report/:id"
                  element={
                    <PackageModuleGuard moduleLabel="Reports">
                      <ZReportDetail />
                    </PackageModuleGuard>
                  }
                />
                {/* Please keep  it here  */}

                <Route path="/manage" element={<Manage />} />
                <Route path="/manage/services" element={<ServicesPage />} />
                <Route
                  path="/manage/categories"
                  element={<CategoriesPageNew />}
                />
                <Route path="/manage/employee" element={<EmployeePage />} />
                <Route
                  path="/manage/employee/scheduled-shifts"
                  element={
                    <PackageModuleGuard moduleLabel="Employee Schedule">
                      <ScheduledShiftsPage />
                    </PackageModuleGuard>
                  }
                />
                <Route
                  path="/manage/employee/timesheets"
                  element={
                    <PackageModuleGuard moduleLabel="Employee Schedule">
                      <TimesheetPage />
                    </PackageModuleGuard>
                  }
                />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/service-settings" element={<ServiceSettings />} />
                <Route path="/manage/products" element={<ProductsPage />} />
                <Route path="/manage/deals" element={<DealsPage />} />
                <Route path="/manage/coupons" element={<CouponsPage />} />
                <Route path="/manage/coupons/create" element={<CouponFormPage />} />
                <Route path="/manage/coupons/:id/edit" element={<CouponFormPage />} />
                <Route
                  path="/manage/product-sales"
                  element={<SoldProductsPage />}
                />
                <Route path="/customers" element={<Customers />} />
                <Route path="/upgrades" element={<Upgrades />} />

                <Route path="/sunbeds">
                  <Route
                    index
                    element={<Navigate to="/sunbeds/packages" replace />}
                  />
                  <Route path="packages" element={<SunbedPackages />} />
                  <Route path="memberships" element={<SunbedMembershipPlans />} />
                  <Route path="sunbeds" element={<SunbedsPage />} />
                  <Route
                    path="customer-packages"
                    element={<CustomerPackages />}
                  />
                </Route>
                <Route path="/sunbed-session" element={<SunbedSessionPage />} />
                <Route
                  path="/sunbed-session/waiting-list"
                  element={<SunbedWaitingListPage />}
                />
                <Route
                  path="/sunbed-sessions-kiosk"
                  element={<SunbedSessionPage kiosk />}
                />

                {/* Settings Routes with nested routes */}
                <Route path="/settings" element={<Settings />}>
                  <Route
                    index
                    element={<Navigate to="/settings/profile" replace />}
                  />
                  <Route path="profile" element={<ProfileSettings />} />
                  <Route path="general" element={<GeneralSettings />} />
                  <Route path="billing" element={<BillingSettings />} />
                  <Route
                    path="attach-domain"
                    element={<AttachDomainSettings />}
                  />
                  <Route path="xonder" element={<XONDERSettings />} />
                  <Route path="vendor-page" element={<VendorPageSettings />} />
                  <Route path="bookings" element={<BookingsSettings />} />
                  <Route
                    path="notification-settings"
                    element={<NotificationSettings />}
                  />
                  <Route
                    path="quick-payments"
                    element={<QuickPaymentsSettings />}
                  />
                  <Route path="sunbed-settings" element={<SunbedSettings />} />
                  <Route path="printer" element={<PrinterSettings />} />
                  <Route path="cash-drawer" element={<CashDrawerSettings />} />
                  <Route
                    path="form-settings"
                    element={<CustomerRegistrationSettings />}
                  />
                  <Route
                    path="form-templates"
                    element={<Navigate to="/settings/form-settings" replace />}
                  />
                  <Route
                    path="customer-registration"
                    element={<Navigate to="/settings/form-settings" replace />}
                  />
                  <Route path="kiosk" element={<KioskSettings />} />
                </Route>

                {/* Website Routes with nested routes */}
                <Route path="/website" element={<Website />}>
                  <Route
                    index
                    element={<Navigate to="/website/templatesection" replace />}
                  />
                  <Route
                    path="templatesection"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <TemplateSection />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="storesetting"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <SectionSettings />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="fronttheme"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <FontandThemeSection />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="herosection"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <HeroSection />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="aboutsection"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <AboutSection />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  {/* <Route
                  path="servicesection"
                  element={
                    <StepProvider>
                      <SectionProvider>
                        <ServiceSection />
                      </SectionProvider>
                    </StepProvider>
                  }
                /> */}
                  <Route
                    path="testimonialsection"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <TestimonialsSection />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="gallerysection"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <GallerySection />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="footersection"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <FooterSection />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  {/* <Route
                    path="successpage"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <SuccessPage />
                        </SectionProvider>
                      </StepProvider>
                    }
                  /> */}
                  <Route
                    path="aboutuspage"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <AboutUsPage />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="cancelpolicy"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <CancelPolicyPage />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="privacypolicy"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <PrivacyPolicyPage />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                  <Route
                    path="termsconditions"
                    element={
                      <StepProvider>
                        <SectionProvider>
                          <TermsConditionsPage />
                        </SectionProvider>
                      </StepProvider>
                    }
                  />
                </Route>
              </Routes>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

// -------------------------------
// Main AppRoutes
// -------------------------------
const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        {/* Fully isolated public routes — no Auth / Sunbed / product APIs */}
        <Route
          path="/business-onboarding/:encodedSubscriptionId"
          element={<BusinessProposalPage />}
        />
        <Route
          path="/business-proposal/:encodedSubscriptionId"
          element={<BusinessProposalPage />}
        />
        <Route
          path="/consultationform/:templateId"
          element={<PublicFormPage />}
        />
        <Route
          path="/forms/consultation/:encodedId"
          element={<PublicConsultationPage />}
        />
        <Route
          path="/forms/register/:encodedId"
          element={<PublicCustomerRegistrationPage />}
        />
        <Route
          path="/website/microsite/:userId"
          element={
            <SectionProvider>
              <Microsite />
            </SectionProvider>
          }
        />

        {/* Authenticated app tree */}
        <Route
          path="/*"
          element={
            <AuthProvider>
              <SunbedSessionsProvider>
                <SunbedTmaxJobPoller />
                <AuthenticatedAppRoutes />
              </SunbedSessionsProvider>
            </AuthProvider>
          }
        />
      </Routes>
    </Router>
  );
};

const AuthenticatedAppRoutes = () => {
  return (
    <Routes>
      {/* Public routes (login/signup) - Redirect if already logged in */}
      <Route
        path="/signup"
        element={<Navigate to="/login" replace />}
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      {/* Chrome-free POS customer display (dual screen) */}
      <Route
        path="/pos/customer-display"
        element={
          <PrivateRoute>
            <CustomerDisplay />
          </PrivateRoute>
        }
      />

      {/* Protected app routes (require authentication) */}
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
